import { AssignmentDTO, AssignmentDTOClient } from 'models/assignment'
import { AssignmentDTOIsAdministratorDTO, AssignmentDTOIsClientDTO } from 'utils/typeguards'
import { DUPLICATE_DEAL_CANCELLATION_ERROR_CODE, DealState } from 'constants/deal'
import { FC, ReactNode, createContext, useContext, useEffect, useMemo } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { ProductKind, ProductKindsWithDocumentGallery, ProductKindsWithDocumentGalleryWithoutMeasurementOnSite, SELF_UPLOAD_PRODUCT_IDS } from 'constants/product'
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useCancelOrder, useGetOrder } from 'dataQueries/order.query'

import { APIRequestErrorType } from 'constants/API'
import { DealDTO } from 'models/deal'
import { Nullable } from 'models/helpers'
import axios from 'axios'
import { earliestByShootingStart } from 'utils/time'
import { isEditingCategory } from 'utils/validators'
import moment from 'moment'
import { liveAssignments, stagesUnlockingOrderCancellation } from 'utils/stages'
import { useAuth0 } from 'utils/auth'
import { useDynamicNow } from 'utils/hooks'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGetOrderMessageThreadCount } from 'dataQueries/messageThreads'

interface GalleryDealInterface {
  order?: UseMutationResult<any, any, any, any> | UseQueryResult<any, any>,
  dealId: number
  dealData?: Nullable<DealDTO>
  dealAssignmentsForTab: AssignmentDTO[]
  stagingAssignments: AssignmentDTO[]
  containsKeysPickup: boolean,
  dealError?: APIRequestErrorType
  isOneStagingSelection: boolean,
  shootingStart: Nullable<string>
  earliestAssignment: Nullable<AssignmentDTOClient>
  requiresPowerOfAttorneyDocuments?: boolean
  orderIncludesDocumentProducts?: boolean
  orderIncludesDocumentProductWithoutMeasurementOnSiteProduct: boolean
  totalRequestCountData?: number
  refetchTotalRequestsCount: () => void
  allowCancelOrder: boolean
}

const defaultGalleryDealValue: GalleryDealInterface = {
  dealId: 0,
  dealAssignmentsForTab: [],
  stagingAssignments: [],
  containsKeysPickup: false,
  isOneStagingSelection: false,
  shootingStart: null,
  earliestAssignment: null,
  requiresPowerOfAttorneyDocuments: false,
  orderIncludesDocumentProducts: false,
  orderIncludesDocumentProductWithoutMeasurementOnSiteProduct: false,
  totalRequestCountData: undefined,
  refetchTotalRequestsCount: () => { },
  allowCancelOrder: false
}

/** Gallery deal context */
export const GalleryDealContext = createContext<GalleryDealInterface>(defaultGalleryDealValue)
/** Gallery deal context hook */
export const useGalleryDeal = (keepPreviousData = false): GalleryDealInterface => useContext(GalleryDealContext)

/** Context provider for gallery deal */
export const GalleryDealContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
  keepPreviousData?: boolean
}> = ({
  children,
  keepPreviousData = false
}) => {
    const { roles } = useAuth0()
    const { assignmentData, assignment } = useGalleryAssignment()
    const { isDocumentProduct } = useGalleryProduct()
    const ignoreCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

    const dealId = useMemo(() => (assignmentData && (AssignmentDTOIsAdministratorDTO(assignmentData) || AssignmentDTOIsClientDTO(assignmentData))) ? assignmentData.dealId : -1, [assignmentData])
    const order = useGetOrder(dealId, dealId > 0 && (roles.isAdmin || roles.isClient), keepPreviousData)

    const dealData = useMemo(() => order.data?.data, [order.data?.data])
    const dealAssignmentsForTab = useMemo(() => dealData?.assignments && dealData.assignments.length ? dealData.assignments.filter((assignment) => !assignment.isDeleted && !assignment.products.some((p) => SELF_UPLOAD_PRODUCT_IDS.has(p.id))).sort((a, b) => a.id.toString().localeCompare(b.id)) : [], [dealData])
    const containsKeysPickup = useMemo(() => !!dealData && dealData?.financials.products.some(product => product.kind === ProductKind.KEYS_PICKUP), [dealData])

    // A cancelled line keeps no shoot: it neither starts the order nor opens the late-cancellation window
    const earliestAssignment = useMemo(() => earliestByShootingStart(liveAssignments(dealData?.assignments, ignoreCancelledLines)), [dealData, ignoreCancelledLines])

    const shootingStart = useMemo(() => earliestAssignment?.timezone
      && earliestAssignment?.shootingStartDateTime ? moment(earliestAssignment.shootingStartDateTime).tz(earliestAssignment.timezone).toISOString() : null, [earliestAssignment])

    /** List of assignments containing STAGING product with exception for STAGING_360 product type */
    const stagingAssignments = useMemo(
      () => dealAssignmentsForTab.filter(
        (assignment) => assignment.products.some(
          (product) => product.isStaging && !product.isStaging360
        )
      ),
      [dealAssignmentsForTab]
    )

    const isOneStagingSelection = useMemo(() => stagingAssignments.length === 1, [stagingAssignments])

    const dealError = useMemo(() => axios.isAxiosError(assignment.error) && assignment.error.response?.data.message, [assignment])

    /** Whether the deal has some assignments with at least one document product kind. */
    const orderIncludesDocumentProducts = useMemo(() => dealData?.assignments.some((assignment) => assignment.products.some((product) => ProductKindsWithDocumentGallery.has(product.kind))), [dealData])

    /** Whether the deal has some assignment with measurement on site product kind. */
    const orderIncludesDocumentProductWithoutMeasurementOnSiteProduct = useMemo(() => dealData?.assignments.some((assignment) => assignment.products.some((product) => ProductKindsWithDocumentGalleryWithoutMeasurementOnSite.has(product.kind))) ?? false, [dealData])

    /** Whether the deal has some assignment with authorities document product kind. */
    const orderIncludeAuthoritiesDocumentsProduct = useMemo(() => dealData?.assignments.some((assignment) => assignment.products.some((product) => product.kind === ProductKind.AUTHORITIES_DOCUMENTS)), [dealData])

    /** Whether the deal requires power of attorney documents and haven't been submitted yet. */
    const requiresPowerOfAttorneyDocuments = useMemo(() =>
      orderIncludeAuthoritiesDocumentsProduct && // Only Authorities documents requires PoA
      dealData?.state !== DealState.CANCELLED &&
      !dealData?.powerOfAttorney.hasDocumentsSubmitted && (dealData?.powerOfAttorney.requiresPowerOfAttorney || dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives),
      [dealData, orderIncludeAuthoritiesDocumentsProduct]
    )

    // CLIENT
    const totalRequestsCount = useGetOrderMessageThreadCount()
    const totalRequestCountData = useMemo(() => totalRequestsCount.data?.data, [totalRequestsCount.data])
    const refetchTotalRequestsCount = () => totalRequestsCount.reset()

    const cancelOrder = useCancelOrder()
    const dynamicNow = useDynamicNow()

    /**
      * Only show the cancel order button when:
      * order is not cancelled AND
      * order has a non-editing category AND
      * order has not started yet AND
      * order has no assignments past stage 4
     */
    const allowCancelOrder = useMemo(() => {
      if (!dealData) return false
      if (dealData.state === DealState.CANCELLED) return false
      if (cancelOrder.data?.data.response?.status === DUPLICATE_DEAL_CANCELLATION_ERROR_CODE) return false
      if (cancelOrder.isSuccess) return false
      if (isEditingCategory(dealData.productCategory)) return false
      if (!!shootingStart && dynamicNow.isSameOrAfter(shootingStart) && !isDocumentProduct) return false
      // Unchanged for clients: a cancelled line at its terminal stage still blocks their own cancel
      if (dealData.assignments.some((assignment) => !stagesUnlockingOrderCancellation.has(assignment.stage))) return false
      if (dealData.assignments.some((assignment) => assignment.products.some(product => product.kind === ProductKind.MEASUREMENT_ON_SITE))) return false

      return true
    }, [cancelOrder.data?.data.response?.status, cancelOrder.isSuccess, dealData, dynamicNow, isDocumentProduct, shootingStart])

    useEffect(() => {
      if (dealId > 0 && totalRequestsCount.status === 'idle') {
        totalRequestsCount.mutate({ orderId: dealId })
      }
      // No need to add dealId as a dependency
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalRequestsCount])

    return (
      <GalleryDealContext.Provider
        value={{
          order,
          dealId,
          dealData,
          dealAssignmentsForTab,
          stagingAssignments,
          containsKeysPickup,
          dealError,
          isOneStagingSelection,
          shootingStart,
          earliestAssignment,
          requiresPowerOfAttorneyDocuments,
          orderIncludesDocumentProducts,
          orderIncludesDocumentProductWithoutMeasurementOnSiteProduct,
          totalRequestCountData,
          refetchTotalRequestsCount,
          allowCancelOrder
        }}
      >
        {children}
      </GalleryDealContext.Provider>
    )
  }
